import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature} from '@aboutyou/tracking';
import {Button, InView} from '@aboutyou/ui-components';
import React, {FC, Fragment, useState} from 'react';
import styled from 'styled-components';
import {getNumerOfChunksPerPaginationPage} from './utils/usePaginationRange';

interface LoadMoreComponentDisplayConfig {
  tilesBeforeFirstDisplay: number;
  tilesBeforeConsecutivelyDisplay: number;
}

interface PageControls {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
  totalPages?: number;
  Wrapper?: FC;
}

const PRODUCTS_PER_PAGE = 32;

/**
 * @deprecated Please use `ProductStreamTokenPagination` instead, especially for new streams
 * @param param0
 * @returns
 */
export const ProductStreamOffsetLimitPagination: React.FC<PageControls> = ({
  currentPage,
  setCurrentPage,
  totalPages,
  loadMoreComponentDisplayConfig,
  Wrapper = Fragment,
}) => {
  const handleLoadMore = (inView: boolean) => {
    if (inView) {
      setCurrentPage(currentPage + 1);
    }
  };

  const {tilesBeforeFirstDisplay} = loadMoreComponentDisplayConfig;

  return (
    <Wrapper>
      {currentPage * PRODUCTS_PER_PAGE < tilesBeforeFirstDisplay ? (
        <InView
          onChange={handleLoadMore}
          data-testid="loadTriggerIntersectionObserver"
          rootMargin="1500px"
        >
          <LoadMoreTriggerBlock
            data-testid={`loadMoreTriggerBlock_${tilesBeforeFirstDisplay}`}
          />
        </InView>
      ) : (
        <LoadMoreProductsWithEndlessScroll
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          totalPages={totalPages}
        />
      )}
    </Wrapper>
  );
};

/*
 * Component implements A/B test variant switch if test is activated
 * Check this ticket for the variants: https://aboutyou.atlassian.net/browse/ASFE-3551
 */
const LoadMoreProductsWithEndlessScroll: React.FC<PageControls> = ({
  currentPage,
  setCurrentPage,
  loadMoreComponentDisplayConfig,
}) => {
  const {t} = useTranslation();
  const [showLoadMoreComponent, setShowLoadMoreComponent] = useState(true);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const trackFeature = useTrackFeature();

  const {tilesBeforeConsecutivelyDisplay} = loadMoreComponentDisplayConfig;

  const pagesToLoadBeforeTheLoadMoreComponentIsShownAgain =
    getNumerOfChunksPerPaginationPage(tilesBeforeConsecutivelyDisplay);

  const handleLoadMoreButtonClick = () => {
    trackFeature({
      eventType: 'click',
      label: 'load_more_stream',
      name: 'load_more_stream',
    });
    setShowLoadMoreComponent(false);
    setPagesLoaded(1);
    setCurrentPage(currentPage + 1);
  };

  const handleEndlessLoad = (inView: boolean) => {
    if (inView) {
      if (pagesLoaded === pagesToLoadBeforeTheLoadMoreComponentIsShownAgain) {
        setShowLoadMoreComponent(true);
        setPagesLoaded(0);
      } else {
        setCurrentPage(currentPage + 1);
        setPagesLoaded(pagesLoaded + 1);
      }
    }
  };

  if (!showLoadMoreComponent) {
    return (
      <Wrapper>
        <InView onChange={handleEndlessLoad} rootMargin="1500px">
          <LoadMoreTriggerBlock
            data-testid={`loadMoreTriggerBlock_${tilesBeforeConsecutivelyDisplay}`}
          />
        </InView>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Button
        data-testid={`loadMoreButton_${tilesBeforeConsecutivelyDisplay}`}
        onClick={handleLoadMoreButtonClick}
      >
        {t('Load more')}
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4rem 0 2rem;
`;

const LoadMoreTriggerBlock = styled.div`
  width: 100%;
  height: 50px;
`;
